// ===========================================
// Pricing Dimension Selector
// ===========================================
// Allows admin to choose the pricing row dimension

import { useState } from 'react';
import {
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import type { PricingDimension } from '../../../api/pricing.api';

// ===========================================
// TYPES
// ===========================================

interface PricingDimensionSelectorProps {
  currentDimension: PricingDimension;
  onDimensionChange: (dimension: PricingDimension) => void;
  isUpdating: boolean;
}

// ===========================================
// DIMENSION OPTIONS
// ===========================================

const DIMENSION_OPTIONS: Array<{
  value: PricingDimension;
  label: string;
  description: string;
}> = [
  {
    value: 'PROGRAM_LEVEL',
    label: 'Program Level',
    description: 'Price by student program level (e.g., Mini, Master, Mezzo)',
  },
  {
    value: 'AGE_GROUP',
    label: 'Age Group',
    description: 'Price by student age group (e.g., Child, Teen, Adult)',
  },
  {
    value: 'INSTRUMENT',
    label: 'Instrument',
    description: 'Price by lesson instrument (e.g., Piano, Guitar, Violin)',
  },
  {
    value: 'PRICE_ONLY',
    label: 'Flat Rate',
    description: 'One price per duration, regardless of level/age/instrument',
  },
];

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function PricingDimensionSelector({
  currentDimension,
  onDimensionChange,
  isUpdating,
}: PricingDimensionSelectorProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDimension, setPendingDimension] = useState<PricingDimension | null>(null);

  const handleChange = (_: React.MouseEvent<HTMLElement>, newDimension: PricingDimension | null) => {
    if (!newDimension || newDimension === currentDimension) return;

    setPendingDimension(newDimension);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (pendingDimension) {
      onDimensionChange(pendingDimension);
    }
    setConfirmOpen(false);
    setPendingDimension(null);
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setPendingDimension(null);
  };

  const currentOption = DIMENSION_OPTIONS.find((o) => o.value === currentDimension);

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Pricing Dimension
        </Typography>

        <ToggleButtonGroup
          value={currentDimension}
          exclusive
          onChange={handleChange}
          disabled={isUpdating}
          size="small"
          sx={{ mb: 1.5 }}
        >
          {DIMENSION_OPTIONS.map((option) => (
            <ToggleButton
              key={option.value}
              value={option.value}
              sx={{ textTransform: 'none', px: 2 }}
            >
              {isUpdating && pendingDimension === option.value ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : null}
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {currentOption && (
          <Typography variant="body2" color="text.secondary">
            {currentOption.description}
          </Typography>
        )}
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={handleCancel}>
        <DialogTitle>Change Pricing Dimension?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Changing the pricing dimension will change which rows are displayed in your pricing
            matrix. Existing prices for the current dimension are preserved and will reappear if
            you switch back.
          </DialogContentText>
          {pendingDimension && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Switching to:{' '}
              <strong>
                {DIMENSION_OPTIONS.find((o) => o.value === pendingDimension)?.label}
              </strong>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
